import { yupResolver } from '@hookform/resolvers/yup'
import { findIndex, forEach, isEqual, map } from 'lodash'
import { ChangeEvent, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { PageTitle } from '@/components'
import { AlertRatio } from '@/components/AlertRatio'
import {
  Button,
  Card,
  Checkbox,
  DateTimePicker,
  Input,
  Modal,
} from '@/components/ui'
import { HtmlEditor } from '@/core/components/HtmlEditor'
import { getTranslation } from '../../../account/services/auth'
import Loader from '../../../common/components/Loader'
import { Article, ArticlePayload, Languages } from '../../../common/models'
import FormGroup from '../../../core/components/FormGroup'
import { FormUploadPreview } from '../../../core/components/FormUploadPreview'
import { formatDateTime } from '../../../core/constants/times'
import {
  createArticle,
  deleteArticles,
  getArticle,
  updateArticle,
} from '../../../core/services/articles'
import translate from '../../../core/services/translate'
import { uploadImage } from '../../../core/services/upload-image'
import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { MessageBox } from '../messages/MessageBox'
import { Icon } from '@/common/components/Icon'

export interface INewsValues {
  content?: INewsContent[]
  publishDate?: string
  published?: boolean
  photo?: string
  thumbPhoto?: string | null
}

interface INewsContent {
  locale: string
  name: string
  metaTitle: string
  metaTitleUrl: string
  metaDescription: string
  metaKeywords: string
  description: string
}

export interface CreateNewsModalProps {
  editMode: boolean
  showCreateModal: boolean
  news?: Article
  page: number
  setEditMode: (edit: boolean) => void
  setShowCreateModal: (show: boolean) => void
  onCreate?: () => void
  onClose?: () => void
  allLanguages: Languages[]
}

export const CreateNewsModal = (props: CreateNewsModalProps): JSX.Element => {
  const [image, setImage] = useState('')
  const [imageName, setImageName] = useState('')
  const [thumb, setThumb] = useState('')
  const [thumbName, setThumbName] = useState('')
  const [showDeleteConfirmationModal, setDeleteConfirmationModal] =
    useState<boolean>(false)
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(true)
  const [invalidPhoto, setInvalidPhoto] = useState<boolean>(false)
  const [invalidPhotoThumb, setInvalidPhotoThumb] = useState<boolean>(false)

  const [isError, setIsError] = useState<boolean>(false)
  const [isErrorRatio, setIsErrorRatio] = useState<boolean>(false)
  const [isErrorRatioThumb, setIsErrorRatioThumb] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const [content, setContent] = useState<INewsContent[]>([])

  const {
    showCreateModal,
    setShowCreateModal,
    news,
    onCreate,
    onClose,
    allLanguages,
  } = props

  const language = getTranslation()

  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        const temp = map(allLanguages, lang => {
          return {
            locale: lang.locale,
            name: '',
            metaTitle: '',
            metaTitleUrl: '',
            metaDescription: '',
            metaKeywords: '',
            description: '',
          }
        })
        setValue('content', temp)
        setLoading(true)
        if (news?.id && showCreateModal) {
          const { status, data } = await getArticle(news.id)
          if (status === 200 && !!data) {
            setValue('publishDate', formatDateTime(new Date().toISOString()))
            const temp: INewsContent[] = []
            Object.keys(data.translations).forEach((key) => {
              const langObject = data.translations[
                key
              ] as unknown as INewsContent & { contents: string }
              temp.push({
                locale: key,
                name: langObject.name,
                metaTitle: langObject.metaTitle,
                metaTitleUrl: langObject.metaTitleUrl,
                metaDescription: langObject.metaDescription,
                metaKeywords: langObject.metaKeywords,
                description: langObject.contents,
              })
            })
            if (!!temp && temp.length > 0) {
              setContent(temp)
              setTimeout(() => {
                map(temp, trans => {
                  const index = findIndex(allLanguages, l => l.locale === trans.locale)
                  if (index !== -1) {
                    setValue(`content.${index}.locale`, trans.locale)
                    setValue(`content.${index}.name`, trans.name)
                    setValue(`content.${index}.metaTitle`, trans.metaTitle)
                    setValue(`content.${index}.metaTitleUrl`, trans.metaTitleUrl)
                    setValue(`content.${index}.metaDescription`, trans.metaDescription)
                    setValue(`content.${index}.metaKeywords`, trans.metaKeywords)
                    setValue(`content.${index}.description`, trans.description)
                  }
                })
              }, 100)
            }
            setValue('published', data.published)
            setValue('publishDate', formatDateTime(data.publishedAt))
            setValue('photo', data.imageLink)
            setValue('thumbPhoto', data.thumbnailLink)
            setThumb(data.thumbnailLink)
            setImage(data.imageLink)
            if (data?.imageLink) {
              const url = new URL(data.imageLink)
              const i = url?.pathname?.lastIndexOf('/') + 1
              const iName = url?.pathname?.substring(i)
              setImageName(iName)
            }
            if (data?.thumbnailLink) {
              const url2 = new URL(data.thumbnailLink)
              const l = url2?.pathname?.lastIndexOf('/') + 1
              const tName = url2?.pathname?.substring(l)
              setThumbName(tName)
            }
          }
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    })()
    return () => {
      setImage('')
      setThumb('')
      setImageName('')
      setThumbName('')
      reset()
    }
  }, [news, showCreateModal])

  const schema = yup
    .object()
    .shape({
      content: yup
        .array()
        .min(1)
        .of(
          yup.object().shape({
            locale: yup.string().required(),
            name: yup.string().required(),
            metaTitle: yup.string().required(),
            metaTitleUrl: yup.string().required(),
            metaDescription: yup.string().required(),
            metaKeywords: yup.string().required(),
            description: yup.string().required(),
          }),
        ),
      published: yup.boolean().required(),
      publishDate: yup.string().required(),
      photo: yup.string().required(),
      thumbPhoto: yup.string().required(),
    })
    .required()

  const {
    handleSubmit,
    register,
    formState: { errors, dirtyFields },
    reset,
    control,
    setValue,
    watch,
  } = useForm<INewsValues>({
    mode: 'onChange',
    resolver: yupResolver<INewsValues>(schema),
    defaultValues: {
      published: false,
    },
  })

  const contentWatch = watch('content')

  const handleClose = () => {
    setShowCreateModal(false)
    setImage('')
    setThumb('')
    setImageName('')
    setThumbName('')
    reset()
    const temp = map(allLanguages, (lang) => {
      return {
        locale: lang.locale,
        name: '',
        metaTitle: '',
        metaTitleUrl: '',
        metaDescription: '',
        metaKeywords: '',
        description: '',
      }
    })
    setValue('content', temp)
    onClose?.()
  }

  const submit = async (values: INewsValues) => {
    const payload: ArticlePayload = {}

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const translations: { [key: string]: any } = {}
    forEach(values.content, (item) => {
      translations[item.locale] = {
        contents: item.description,
        name: item.name,
        metaTitle: item.metaTitle,
        metaTitleUrl: item.metaTitleUrl,
        metaDescription: item.metaDescription,
        metaKeywords: item.metaKeywords,
      }
    })

    payload.published = values.published
    payload.translations = translations
    payload.publishedAt = new Date(values.publishDate as string).getTime()
    payload.imageLink = values.photo as string
    payload.thumbnailLink = values.thumbPhoto as string

    try {
      setSubmitting(true)
      const { status } = news?.id
        ? await updateArticle(news.id, payload)
        : await createArticle(payload)
      if (status === 200) {
        handleClose()
        onCreate?.()
        setIsError(false)
        setShowMessageBox(true)
        setMessage(news?.id ? 'Update Succesful' : 'Create Successful')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async () => {
    if (!news) return
    try {
      const { status } = await deleteArticles([news?.id])
      if (status === 204) {
        setDeleteConfirmationModal(false)
        handleClose()
        onCreate?.()
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Delete Successful')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    }
  }

  return (
    <>
      {loading && <Loader />}
      <Modal
        size="xl"
        open={showCreateModal}
        onOpenChange={setShowCreateModal}
        title={
          <div className="border-b border-monochrome-120 pb-4">
            <PageTitle
              icon="news"
              title={news?.id ? 'Edit News' : 'Add News'}
            />
          </div>
        }
        footer={
          <div className="flex w-full items-center gap-2 justify-between">
            {!!news && (
              <Button
                onClick={() => setDeleteConfirmationModal(true)}
                variant="destructive"
              >
                Delete
                <Icon
                  icon="close-circle"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                />
              </Button>
            )}
            <div className="flex w-full items-center gap-2 justify-end mt-4">
              <Button onClick={handleClose} variant="secondary">
                Cancel
              </Button>
              <Button
                disabled={
                  isUploading ||
                  submitting ||
                  (Object.keys(dirtyFields).length === 0 &&
                    isEqual(contentWatch, content))
                }
                variant="primary"
                form="news-form"
                type="submit"
              >
                Save
              </Button>
            </div>
          </div>
        }
      >
        <form
          id="news-form"
          className="space-y-7"
          onSubmit={handleSubmit(submit)}
        >
          <h2 className="text-2xl font-semibold text-monochrome-1">
            Main Info
          </h2>
          {map(allLanguages, (language, index) => {
            return (
              <Card
                className="p-4 space-y-2 bg-monochrome-120 border-none"
                key={language.id}
              >
                <FormGroup
                  label="Language"
                  wrapperClassName="mb-2"
                  labelClassName="whitespace-nowrap"
                >
                  <Input
                    value={language.name}
                    disabled
                    className="bg-monochrome-120 border-none"
                  />
                </FormGroup>
                <FormGroup label="Name">
                  <Input
                    isInvalid={!!errors?.content?.[index]?.name?.message}
                    {...register(`content.${index}.name`)}
                    type="text"
                    placeholder="Enter Name"
                  />
                </FormGroup>
                <FormGroup label="Meta Title">
                  <Input
                    isInvalid={!!errors?.content?.[index]?.metaTitle?.message}
                    {...register(`content.${index}.metaTitle`)}
                    type="text"
                    placeholder="Enter Meta Title"
                  />
                </FormGroup>
                <FormGroup label="Meta Title URL">
                  <Input
                    isInvalid={
                      !!errors?.content?.[index]?.metaTitleUrl?.message
                    }
                    {...register(`content.${index}.metaTitleUrl`)}
                    type="text"
                    placeholder="Enter Meta Title URL"
                  />
                </FormGroup>
                <FormGroup label="Meta Description">
                  <Input
                    isInvalid={
                      !!errors?.content?.[index]?.metaDescription?.message
                    }
                    {...register(`content.${index}.metaDescription`)}
                    type="text"
                    placeholder="Enter Meta Description"
                  />
                </FormGroup>
                <FormGroup label="Meta Keywords">
                  <Input
                    isInvalid={
                      !!errors?.content?.[index]?.metaKeywords?.message
                    }
                    {...register(`content.${index}.metaKeywords`)}
                    type="text"
                    placeholder="Enter Meta Description"
                  />
                </FormGroup>
                <HtmlEditor
                  index={index}
                  label="Description"
                  name={`content.${index}.description`}
                  variables={[]}
                  control={control}
                  value={contentWatch?.[index]?.description || ''}
                  onChange={(data) => {
                    setValue(`content.${index}.description`, data)
                  }}
                  isError={!!errors?.content?.[index]?.description?.message}
                />
              </Card>
            )
          })}
          <Controller
            name="publishDate"
            control={control}
            render={({ field }) => (
              <FormGroup
                label="Publish Date"
                tooltip={translate(language.value, 'news.publishDateTooltip')}
              >
                <DateTimePicker
                  className="w-full"
                  placeholder="Enter Publish Date"
                  hasError={!!errors?.publishDate?.message}
                  value={field.value ? new Date(field.value) : null}
                  onChange={(date) =>
                    field.onChange(date ? date.toISOString() : undefined)
                  }
                />
              </FormGroup>
            )}
          />

          <FormGroup
            label="Published"
            direction="row"
            labelClassName="whitespace-nowrap"
          >
            <Controller
              name="published"
              control={control}
              render={({ field }) => (
                <Checkbox
                  className="border-monochrome-1"
                  checked={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </FormGroup>

          <hr className="my-4 h-px w-full border-monochrome-120" />

          <FormUploadPreview
            formGroupProps={{
              labelStyle: { minWidth: '124px' },
            }}
            direction="row"
            control={
              <Input
                multiple={false}
                accept="image/*"
                isInvalid={
                  !!errors?.photo?.message || invalidPhoto || isErrorRatio
                }
                name="photo"
                onChange={async (e: ChangeEvent<HTMLInputElement>) => {
                  const file = e?.target?.files?.[0]
                  if (file) {
                    if (file.size > 1_000_000) {
                      setInvalidPhoto(true)
                      setIsError(true)
                      setShowMessageBox(true)
                      setMessage('The photo is too large (1 MB)')
                      return
                    }
                    const data = await uploadImage(
                      file,
                      setIsUploading,
                      setIsError,
                      setShowMessageBox,
                      setMessage,
                      setIsErrorRatio,
                      'NEWS',
                    )
                    if (data.photoUrl) {
                      setImage(data.photoUrl)
                      setImageName(file.name)
                      setValue('photo', data.photoUrl, {
                        shouldValidate: true,
                        shouldDirty: true,
                      })
                    }
                  }
                }}
                type="file"
              />
            }
            imageName={imageName}
            isUploading={isUploading}
            url={image}
            onRemove={() => {
              setImage('')
              setImageName('')
              setValue('photo', '')
            }}
            imageStyle={{
              objectFit: 'contain',
            }}
            label="Image (1010X400)"
            previewLabel="Image (1010X400)"
          />
          {isErrorRatio && <AlertRatio />}

          <FormUploadPreview
            direction="row"
            formGroupProps={{ labelStyle: { maxWidth: '124px' } }}
            control={
              <Input
                multiple={false}
                accept="image/*"
                isInvalid={
                  !!errors?.thumbPhoto?.message ||
                  invalidPhotoThumb ||
                  isErrorRatioThumb
                }
                name="thumbPhoto"
                onChange={async (e: ChangeEvent<HTMLInputElement>) => {
                  const file = e?.target?.files?.[0]
                  if (file) {
                    if (file.size > 1_000_000) {
                      setInvalidPhotoThumb(true)
                      setIsError(true)
                      setShowMessageBox(true)
                      setMessage('The photo is too large (1 MB)')
                      return
                    }
                    const data = await uploadImage(
                      file,
                      setIsUploading,
                      setIsError,
                      setShowMessageBox,
                      setMessage,
                      setIsErrorRatioThumb,
                      'NEWS_THUMBNAIL',
                    )
                    if (data.photoUrl) {
                      setThumb(data.photoUrl)
                      setThumbName(file.name)
                      setValue('thumbPhoto', data.photoUrl, {
                        shouldValidate: true,
                        shouldDirty: true,
                      })
                    }
                  }
                }}
                type="file"
              />
            }
            imageName={thumbName}
            isUploading={isUploading}
            url={thumb}
            onRemove={() => {
              setThumb('')
              setThumbName('')
              setValue('thumbPhoto', '')
            }}
            imageStyle={{
              objectFit: 'contain',
            }}
            label="Thumbnail (400X300)"
            previewLabel="Thumbnail (400X300)"
          />

          {isErrorRatioThumb && <AlertRatio />}

          {/* 
          <hr className="m-0 w-100" />
          <FormUploadPreview
            formGroupProps={{
              labelStyle: { minWidth: '124px' },
            }}
            direction="row"
            control={
              <Form.Control
                multiple={false}
                accept="image/*"
                isInvalid={
                  !!errors?.photo?.message || invalidPhoto || isErrorRatio
                }
                name="photo"
                onChange={async (e: ChangeEvent<HTMLInputElement>) => {
                  const file = e?.target?.files?.[0]
                  if (file) {
                    if (file.size > 1_000_000) {
                      setInvalidPhoto(true)
                      setIsError(true)
                      setShowMessageBox(true)
                      setMessage('The photo is too large (1 MB)')
                      return
                    }
                    const data = await uploadImage(
                      file,
                      setIsUploading,
                      setIsError,
                      setShowMessageBox,
                      setMessage,
                      setIsErrorRatio,
                      'NEWS',
                    )
                    if (data.photoUrl) {
                      setImage(data.photoUrl)
                      setImageName(file.name)
                      setValue('photo', data.photoUrl, {
                        shouldValidate: true,
                        shouldDirty: true,
                      })
                    }
                  }
                }}
                type="file"
              />
            }
            imageName={imageName}
            isUploading={isUploading}
            url={image}
            onRemove={() => {
              setImage('')
              setImageName('')
              setValue('photo', '')
            }}
            imageStyle={{
              objectFit: 'contain',
            }}
            label="Image (1010X400)"
            previewLabel="Image (1010X400)"
          />
          {isErrorRatio && (
            <div
              className="p-3 flex flex-row items-center"
              style={{
                background: '#F8D7DA',
                border: '1px solid #F5C2C7',
                width: '100%',
              }}
            >
              <i
                className="bi bi-info-circle-fill me-2"
                style={{ color: '#842029' }}
              />
              <span
                style={{
                  width: '100%',
                  color: '#842029',
                }}
              >
                The image uploaded has a different aspect ration than required.
                Please try again.
              </span>
            </div>
          )}
          <FormUploadPreview
            direction="row"
            formGroupProps={{ labelStyle: { maxWidth: '124px' } }}
            control={
              <Form.Control
                multiple={false}
                accept="image/*"
                isInvalid={
                  !!errors?.thumbPhoto?.message ||
                  invalidPhotoThumb ||
                  isErrorRatioThumb
                }
                name="thumbPhoto"
                onChange={async (e: ChangeEvent<HTMLInputElement>) => {
                  const file = e?.target?.files?.[0]
                  if (file) {
                    if (file.size > 1_000_000) {
                      setInvalidPhotoThumb(true)
                      setIsError(true)
                      setShowMessageBox(true)
                      setMessage('The photo is too large (1 MB)')
                      return
                    }
                    const data = await uploadImage(
                      file,
                      setIsUploading,
                      setIsError,
                      setShowMessageBox,
                      setMessage,
                      setIsErrorRatioThumb,
                      'NEWS_THUMBNAIL',
                    )
                    if (data.photoUrl) {
                      setThumb(data.photoUrl)
                      setThumbName(file.name)
                      setValue('thumbPhoto', data.photoUrl, {
                        shouldValidate: true,
                        shouldDirty: true,
                      })
                    }
                  }
                }}
                type="file"
              />
            }
            imageName={thumbName}
            isUploading={isUploading}
            url={thumb}
            onRemove={() => {
              setThumb('')
              setThumbName('')
              setValue('thumbPhoto', '')
            }}
            imageStyle={{
              objectFit: 'contain',
            }}
            label="Thumbnail (400X300)"
            previewLabel="Thumbnail (400X300)"
          />

          {isErrorRatioThumb && (
            <div
              className="p-3 flex flex-row items-center"
              style={{
                background: '#F8D7DA',
                border: '1px solid #F5C2C7',
                width: '100%',
              }}
            >
              <i
                className="bi bi-info-circle-fill me-2"
                style={{ color: '#842029' }}
              />
              <span
                style={{
                  width: '100%',
                  color: '#842029',
                }}
              >
                The image uploaded has a different aspect ration than required.
                Please try again.
              </span>
            </div>
          )} */}
        </form>
      </Modal>

      <DeleteConfirmationModal
        showDeleteConfirmationModal={showDeleteConfirmationModal}
        setDeleteConfirmationModal={setDeleteConfirmationModal}
        deleteTitle={'News'}
        deleteFunc={handleDelete}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
      />
    </>
  )
}
